class DynamicVideoComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializePlayFunctionality();
  }

  initializePlayFunctionality() {
    const playButton = this.querySelector('#play_button');
    const coverImage = this.querySelector('#ovrly_img');
    const video = this.querySelector('iframe');

    const playVideo = () => {
      if (video) {
        this.playIframeVideo(video);
      }
      // Hide play button and cover image on play
      if (playButton) {
        playButton.style.display = 'none';
      }
      if (coverImage) {
        coverImage.style.display = 'none';
      }
    };

    // Add event listeners to play button and cover image to play video on click
    if (playButton) {
      playButton.addEventListener('click', playVideo);
    }
    if (coverImage) {
      coverImage.addEventListener('click', playVideo);
    }
  }

  playIframeVideo(video) {
    // Check the video URL to determine if it's YouTube or Vimeo
    const videoSrc = video.src;
    if (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be')) {
      // YouTube video play command
      video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (videoSrc.includes('vimeo.com')) {
      // Vimeo video play command
      video.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
    }
  }
}
if (!window.customElements.get("dynamic-video")) {
  window.customElements.define('dynamic-video', DynamicVideoComponent);
}

class PaymentInstalments extends HTMLElement {
  constructor() {
    super(); 
    this.modalContainer = this.querySelector('.instalments-modal-container');
    this.modal = this.modalContainer.querySelector('.instalments-modal');
    this.modalOverlay = this.modalContainer.querySelector('.instalments-modal__overlay');
    this.modalToggles = this.querySelectorAll('.instalments__modal-trigger');

    // Store the original parent and next sibling for restoring position
    this.originalParent = this.modalContainer.parentNode;
    this.originalNextSibling = this.modalContainer.nextSibling;

    this.attachEventListeners();
  }

  attachEventListeners() {
    this.modalToggles.forEach(toggle => {
      toggle.addEventListener('click', () => this.openModal());
    });

    document.addEventListener('variant:change', (event) => this.handleVariantChange(event));
    document.addEventListener('cartcount:update', (event) => this.handleCartChange(event));
  }

  openModal() {
    // prevent any window refresh
    event.preventDefault();

    // Save the element that was focused before opening the modal
    this.focusedElementBeforeModal = document.activeElement;

    // add the lock class to the html element to prevent scrolling
    document.documentElement.classList.add('lock-scrolling');

    // Move the modal container to the end of the body element
    document.body.appendChild(this.modalContainer);

    // Event listeners for modal functionality
    this.modal.addEventListener('keydown', (e) => this.trapTabKey(e));
    this.modalOverlay.addEventListener('click', () => this.closeModal());

    // Close buttons inside modal
    const closeButtons = this.modal.querySelectorAll('.instalments-modal__close');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closeModal());
    });

    // Show the modal and overlay
    this.modal.style.display = 'block';
    this.modalOverlay.style.display = 'block';
    this.modal.classList.remove('modal-closed');

    // Set focus to the first focusable element in the modal
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  closeModal() {
    // prevent any window refresh
    event.preventDefault();

    // remove the lock class to the html element to prevent scrolling
    document.documentElement.classList.remove('lock-scrolling');

    // Return the modal container to its original position
    if (this.originalNextSibling) {
      this.originalParent.insertBefore(this.modalContainer, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(this.modalContainer);
    }

    this.modal.classList.add('modal-closed');
    this.modal.style.display = 'none';
    this.modalOverlay.style.display = 'none';

    // Return focus to the element that had it before the modal was opened
    if (this.focusedElementBeforeModal) {
      this.focusedElementBeforeModal.focus();
    }
  }

  trapTabKey(e) {
    const focusableElements = this.getFocusableElements();
    const firstTabStop = focusableElements[0];
    const lastTabStop = focusableElements[focusableElements.length - 1];

    if (e.keyCode === 9) { 
      if (e.shiftKey) { 
        if (document.activeElement === firstTabStop) {
          e.preventDefault();
          lastTabStop.focus();
        }
      } else {
        if (document.activeElement === lastTabStop) {
          e.preventDefault();
          firstTabStop.focus();
        }
      }
    }

    if (e.keyCode === 27) {
      this.closeModal();
    }
  }

  handleCartChange(event) {
    const cartTotal = event.detail.response.total_price;
    const instalmentElements = this.querySelectorAll('#instalmentAmountFormatted');
    const money_format = document.documentElement.getAttribute('data-money-format');

    instalmentElements.forEach(element => {
      const instalmentsCount = this.getAttribute('data-instalments-count');
      if (instalmentsCount === 0) {
        return;
      }
      const instalmentAmount = cartTotal / instalmentsCount;

      // Update the displayed instalment amount
      element.textContent = Shopify.formatMoney(
        instalmentAmount,
        money_format
      );
    });
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;
    const instalmentElements = this.querySelectorAll('#instalmentAmountFormatted');
    const money_format = document.documentElement.getAttribute('data-money-format');

    instalmentElements.forEach(element => {
      const instalmentsCount = this.getAttribute('data-instalments-count');
      if (instalmentsCount === 0) {
        return;
      }
      const instalmentAmount = variant.price / instalmentsCount;

      // Update the displayed instalment amount
      element.textContent = Shopify.formatMoney(
        instalmentAmount,
        money_format
      );
    });
  }

  getFocusableElements() {
    const focusableElementsString = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
    return Array.from(this.modal.querySelectorAll(focusableElementsString));
  }
}

if (!window.customElements.get('payment-instalments')) {
  window.customElements.define('payment-instalments', PaymentInstalments);
}

class ParallaxBackground extends HTMLElement {
  constructor() {
    super();
    this.lastKnownScrollPosition = 0;
    this.ticking = false;
  }

  connectedCallback() {
    this.initParallax();
  }

  initParallax() {
    const parallaxBackground = this.querySelector('.parallax__background');
    const mobileThreshold = 0.43;
    const tabletThreshold = 0.57;
    const desktopThreshold = 0.65;
    const downAnimationOnly = false;
    let sectionHeight = this.offsetHeight;
    let isMobile = window.innerWidth <= 768;
    let isTablet = window.innerWidth <= 1200;
    let threshold = isMobile ? mobileThreshold : isTablet ? tabletThreshold : desktopThreshold;

    const updateThreshold = () => {
      isMobile = window.innerWidth <= 768;
      isTablet = window.innerWidth <= 1200;
      threshold = isMobile ? mobileThreshold : isTablet ? tabletThreshold : desktopThreshold;
      sectionHeight = this.offsetHeight;
    };

    const updateBackgroundHeight = (scrollPos) => {
      const windowHeight = window.innerHeight;
      const sectionTop = this.offsetTop;
      const startAnimationAt = sectionTop - windowHeight * threshold;
      const endAnimationAt = sectionTop + sectionHeight - windowHeight * threshold;

      let newHeight = 0;
      if (scrollPos > startAnimationAt && scrollPos < endAnimationAt) {
        let progress = (scrollPos - startAnimationAt) / (endAnimationAt - startAnimationAt);
        newHeight = progress * sectionHeight;
      } else if (scrollPos >= endAnimationAt) {
        newHeight = sectionHeight;
      }
      let current = parseInt(parallaxBackground.style.height, 10);
      if (isNaN(current) || newHeight > current || !downAnimationOnly) {
        parallaxBackground.style.height = `${newHeight}px`;
      }
    };

    window.addEventListener('resize', () => {
      updateThreshold();
    });

    window.addEventListener('scroll', () => {
      this.lastKnownScrollPosition = window.scrollY;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          updateBackgroundHeight(this.lastKnownScrollPosition);
          this.ticking = false;
        });

        this.ticking = true;
      }
    });

    window.addEventListener('load', () => {
      this.lastKnownScrollPosition = window.scrollY;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          updateBackgroundHeight(this.lastKnownScrollPosition);
          this.ticking = false;
        });

        this.ticking = true;
      }
    });
  }
}
if (!window.customElements.get("parallax-background")) {
  customElements.define('parallax-background', ParallaxBackground);
}

const invertedMediaQuery = window.matchMedia("(inverted-colors: inverted)");

function handleColorInversionChange(event) {
  const default_logo = document.querySelector('.site-logo-image');
  const inverted_logo = document.querySelector('.site-logo-image.site-logo-image--inverted');

  if (!default_Logo || !inverted_Logo) return;

  if (event.matches) {
    // hide the default logo and show the inverted logo 
    default_logo.style.display = 'none';
    inverted_logo.style.display = 'block';
  } else {
    // hide the inverted logo and show the default logo 
    default_logo.style.display = 'block';
    inverted_logo.style.display = 'none';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  handleColorInversionChange(invertedMediaQuery);          
  invertedMediaQuery.addEventListener('change', handleColorInversionChange);
});

// Shopify Embedded Form CSS Solution

if (document.body.classList.contains('page-corporate-bulk-ordering')) {
  const intervalId = setInterval(() => {
    const form = document.querySelector('#app-embed');

    if (form && form.shadowRoot) {
      clearInterval(intervalId);

      const shadow = form.shadowRoot;

      const sheet = new CSSStyleSheet();
      sheet.insertRule(`
        h2._textHeading_2aowh_35 {
          font-family: rakuten-sans, sans-serif;
          font-size: 30px;
          margin-block-end: 20px;
        }
      `, 0);
      sheet.insertRule(`
        @media (max-width: 768px) {
          h2._textHeading_2aowh_35 {
            font-size: 24px;
          }
        }
      `, 1);
      sheet.insertRule(`
        p {
          font-family: rakuten-sans, sans-serif;
          font-size: 16px;
          line-height: 1.5625rem;
        }
      `, 2);
      sheet.insertRule(`
        input, label, button {
          font-family: rakuten-sans, sans-serif !important;
        }
      `, 3);

      shadow.adoptedStyleSheets.push(sheet);
    }
  }, 200); 
}

export {
  DynamicVideoComponent,
  ParallaxBackground,
  PaymentInstalments
}

/* START ::: Search */

$(document).ready(function(){
  $(".site-header-search--button").click(function(e){
    e.preventDefault();
    if($(".autocomplete-search-result-outer").hasClass("active")) {
      $(".autocomplete-search-result-outer").css("display", "none");
      $(".autocomplete-search-result-outer").removeClass("active");
      $("body").removeClass("mobile-auto-search-active");
    } else {
      
      $(".autocomplete-search-textbox").focus();
      $(".autocomplete-search-result-outer").addClass("active");
      $(".autocomplete-search-result-outer").css("display", "block");

      
      $("body").addClass("mobile-auto-search-active");
    }
    if($(".autocomplete-search-textbox").length > 0){
      $(".autocomplete-search-textbox").focus();
    }
    handlecustomevent();
  });
  
  document.querySelector('.close-button button').addEventListener('click', function(e) {
    e.preventDefault();
  	document.querySelector('.autocomplete-search-result-outer .search-input .autocomplete-search-textbox').value = '';
    document.querySelector('.autocomplete-search-result-outer .search-result-box.search-empty-case').style.display = 'block';
    document.querySelector('.autocomplete-search-result-outer .search-result-box.search-fill-case').style.display = 'none';

    document.querySelector('.autocomplete-search-result-container .mobile-popup-close-button').classList.add('show-button');
    document.querySelector('.autocomplete-search-result-container .mobile-popup-close-button').classList.remove('hide-button');
  });

  $(".mobile-popup-close-button button").click(function(){
    $('.site-header-search--button').trigger('click');
  });

  $(".autocomplete-search-result-container .search-input-box input").keyup(function(){    
      if($(this).val() != ""){
        $(".autocomplete-search-result-container .mobile-popup-close-button").addClass("hide-button");
        $(".autocomplete-search-result-container .mobile-popup-close-button").removeClass("show-button");
      }else{
        $(".autocomplete-search-result-container .mobile-popup-close-button").removeClass("hide-button");
        $(".autocomplete-search-result-container .mobile-popup-close-button").addClass("show-button");
      }
  });

  document.addEventListener('click', function(event) {
      var targetElement = document.querySelector('.autocomplete-search-result-outer');
      var excludeButton = document.querySelector('.site-header-search--button');
        
      if (targetElement && excludeButton) {          
          if (!targetElement.contains(event.target) && !excludeButton.contains(event.target)) {
            if(targetElement.classList.contains('active')){
              event.preventDefault();
              document.querySelector('.site-header-search--button').click();
            }
          }
      }
  });

   $(".product-page-btn").click(function(e){

    e.preventDefault();

    var hiddenInput = $('.productgrid--search .productgrid--search-form').find('input[type="hidden"]');
    var curreSearchVal = $('.form-field-input.productgrid--search-form-field').attr('data-value'); 

    if($('.form-field-input.productgrid--search-form-field').val() == ""){
      $('.form-field-input.productgrid--search-form-field').val(curreSearchVal);  
    }

    var currectEle = $(this);
    if(currectEle.hasClass('page-post-button')){
      if(hiddenInput.length > 0){
          hiddenInput.val("article,page");
          $(".productgrid--search-form").submit();
      }    
    }else{
      if(hiddenInput.length > 0){
          hiddenInput.val("product");
          $(".productgrid--search-form").submit();
      }
    }
    
  });


  /* Search result page changes Start */
  const fetchResults = (url) => {
      return fetch(url)
          .then(response => {
              if (!response.ok) {
                  throw new Error('Network response was not ok');
              }
              return response.json();
          });
  };
  
  var inputElement = document.querySelector(".form-field-input.productgrid--search-form-field");
  var searchTerms = inputElement ? inputElement.dataset.value : undefined;
  
  var requestHost = window.location.host;
  if(typeof(searchType) != "undefined"){
    if (searchType.includes("product")) {
        document.querySelector('.productgrid--results-button .product-button').style.backgroundColor = '#BDBDBD';
        fetchResults('https://'+requestHost+'/search?q='+searchTerms+'&type=page&view=resultcount')
            .then(pageData => {
                $(".page-post-button").attr("data-hidden-value", pageData.result_count[0]);
                return fetchResults('https://'+requestHost+'/search?q='+searchTerms+'&type=article&view=resultcount');
            })
            .then(articleData => {
                const pageValue = $(".page-post-button").data("hidden-value");
                
                const pageAndPostValue = pageValue + articleData.result_count[0];             
                let pageText = pageValue > 1 ? "Pages" : "Page";
                let buttonText = pageValue <= 0 ? "Post" : articleData.result_count[0] <= 0 ? pageText : `${pageText} & Post`;
            let productCount = $('.page-post-button-value').text()             
            $(".product-button").attr('aria-label',`${productCount} Products  selected`);

                if(pageAndPostValue > 0){
                  $(".page-post-button .page-post-button-value").html(pageAndPostValue);
                  $(".page-post-button .page-post-button-value-text").html(buttonText);
                  $(".productgrid--results-button").css("display", "block");              
                }else{
                  $(".productgrid--results-button").css("display", "block");
                  $(".template-search .productgrid--wrapper .productgrid--results-button .page-post-button").css("display", "none");            
                }
            }).catch(error => console.error(error));
    } else {
        let valueInput = document.querySelector('.form-field-input.productgrid--search-form-field').getAttribute('data-value');
        document.querySelector('.productgrid--results-button .page-post-button').style.backgroundColor = '#BDBDBD';
        fetchResults('https://'+requestHost+'/search?q='+valueInput+'*&type=product&view=resultcount')
            .then(data => {
               let pagesCount = $('.page-post-button-value').text()
                $(".page-post-button ").attr('aria-label',`${pagesCount} Pages  & Post  Selected`);
              
                $(".product-button .product-button-value").html(data.result_count[0]);    

                if(data.result_count[0] > 0){
                  $(".productgrid--results-button").css("display", "block");
                }
            }).catch(error => console.error(error));  
    }
  }
  /* Search result page changes End */



  /* Quick Search API Changes - Start */ 
  function handlecustomevent(){
    var inputElementAutoComplete = document.querySelector(".autocomplete-search-textbox");  
    inputElementAutoComplete.addEventListener('input',function(e) {      
      getSearchDebounce(e.target.value); 
    });
  }
  

  var searchRedirectText = '';
  function modifySearch(result, query) {
    var re = new RegExp(query.split("").map(function(x) { 
        return x.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'); 
    }).join("[-\\s.]*"), 'ig');
    return result.replace(re, '<b>$&</b>');
  }
  
  async function getSearch(e = "") {
    if (e == "") {      
      if (document?.querySelector(".search-empty-case")) {
        document.querySelector(".search-empty-case").style.display = "block";
      }
      if (document.querySelector(".search-fill-case")) {
        document.querySelector(".search-fill-case").style.display = "none";
      }
    } else {
      e = e.trim();
      searchRedirectText = "https://"+window.location.host + "/search?q="+e;
      if (document?.querySelector(".search-empty-case")) {
        document.querySelector(".search-empty-case").style.display = "none";
      }
      if (document.querySelector(".search-fill-case")) {
        let searchUrl = new URL("https://"+window.location.host+ "/search/suggest.json");
        if (searchUrl?.searchParams?.set) {
          searchUrl.searchParams.set("q", e+"*");
          searchUrl.searchParams.set(
            "resources[type]",
            "product,query"
          );
        }
        let resp = {};
        try {
          resp = await fetch(searchUrl, { method: "GET" });
          resp = await resp.json();
        } catch (error) {
          console.log("catch", error);
          resp = {};
        }
        if(document?.querySelector('.autocomplete-search-textbox')?.value!=''){
          if (Object.keys(resp)?.length && resp?.resources?.results) {            
            //for suggestions
            if (resp?.resources?.results?.queries?.length) {
              let suggestion_list = document.querySelector(
                ".search-suggestions-list"
              );
              if(document.querySelector('.full-suggestion-case') && document.querySelector('.empty-suggestion-case')){
                document.querySelector('.full-suggestion-case').style.display= 'block';
                document.querySelector('.empty-suggestion-case').style.display= 'none';
              }
              
              suggestion_list.innerHTML = "";
              
              resp.resources.results.queries.forEach((item) => {
                let liList = document.createElement("li");
                liList.setAttribute("class", "quick-search-kobo");
                let anchorTag = document.createElement("a");
                anchorTag.setAttribute("href", item.url);
                anchorTag.setAttribute("class", "quick-search-kobo");
                let anchor_inner = modifySearch(item.text, e);
                anchorTag.innerHTML = anchor_inner;
                liList.appendChild(anchorTag);
                suggestion_list.appendChild(liList);
              });

               /* Arrow Key up/down Accessiblity Start  */

              $('.quick-search-kobo a').keydown(function(e) {
                  if (e.which === 38) { // Up arrow key
                    $(this).parent('li').prev('li').find('a').focus()
              
                  }
              
                  if (e.which === 40) { // Down arrow key
                    $(this).parent('li').next('li').find('a').focus()
                    if($(this).parent('li').next('li').length==0){
                        $(".autocomplete-search-result-outer").css("display", "none");
                        $(".autocomplete-search-result-outer").removeClass('active');                   
                  }
                    
                  }
              });
              
              /* Arrow Key up/down Accessiblity End  */
            }
            else{
              if(document.querySelector('.full-suggestion-case') && document.querySelector('.empty-suggestion-case')){
                document.querySelector('.full-suggestion-case').style.display= 'none';
                document.querySelector('.empty-suggestion-case').style.display= 'block';
              }
            }
    
            //for products
            if (resp?.resources?.results?.products?.length) {
              let product_list = document.querySelector(".search-product-list");
              if(document.querySelector('.full-product-case') && document.querySelector('.empty-product-case')){
                document.querySelector('.full-product-case').style.display= 'block';
                document.querySelector('.empty-product-case').style.display= 'none';
              }
              product_list.innerHTML = "";
              resp.resources.results.products.forEach((item) => {
                let liList = document.createElement("li");
                liList.setAttribute("class", "product-item quick-search-kobo");
    
                let divList = document.createElement("div");
                divList.setAttribute("class", "product-img");
    
                let imageTag = document.createElement("img");
                imageTag.setAttribute("src", item.image);
                imageTag.setAttribute("width", "43");
                imageTag.setAttribute("height", "43");
    
                divList.appendChild(imageTag);
    
                let productInfo = document.createElement("div");
                productInfo.setAttribute("class", "product-info quick-search-kobo");
    
                let productPrice = document.createElement("div");
                productPrice.setAttribute("class", "price quick-search-kobo");
                productPrice.innerHTML = '$ '+item.price.toString();
    
                let productTitle = document.createElement("div");
                productTitle.setAttribute("class", "name");
                let product_inner = item.title;
                product_inner = modifySearch(product_inner, e);                
                productTitle.innerHTML = product_inner;
    
                productInfo.appendChild(productTitle);
                productInfo.appendChild(productPrice);
                
                let anchorTag = document.createElement("a");
                anchorTag.setAttribute("href", item.url);
                anchorTag.setAttribute("class", "product-anchor quick-search-kobo");
                
                anchorTag.appendChild(divList);
                anchorTag.appendChild(productInfo);
                liList.appendChild(anchorTag);
    
                product_list.appendChild(liList);
              });
              /* Arrow Key up/down Accessiblity Start  */
              $('.quick-search-kobo a').keydown(function(e) {
                  if (e.which === 38) { // Up arrow key
                    $(this).parent('li').prev('li').find('a').focus()
                  }
        
                  if (e.which === 40) { // Down arrow key
                    $(this).parent('li').next('li').find('a').focus()  
                    if($(this).parent('li').next('li').length==0){
                        $(".autocomplete-search-result-outer").css("display", "none");
                        $(".autocomplete-search-result-outer").removeClass('active');
                      }
                  }
              });
              /* Arrow Key up/down Accessiblity End  */
            }
            else{
              if(document.querySelector('.full-product-case') && document.querySelector('.empty-product-case')){
                document.querySelector('.full-product-case').style.display= 'none';
                document.querySelector('.empty-product-case').style.display= 'block';
                document.querySelector('.empty-product-case').style.position= 'relative';
              }
            }

            let resultElement = document.getElementById("globalnav-searchresults-count")
            let prevResult = resultElement.textContent;
              
            setTimeout(() => {
              if(prevResult.endsWith('.')){
               resultElement.textContent = `${resp.resources.results.queries.length + resp?.resources?.results?.products?.length} total results`;                
                  }
              else{
              resultElement.textContent = `${resp.resources.results.queries.length + resp?.resources?.results?.products?.length} total results.`;                
              }
            }, 100);
            
            if(resp?.resources?.results?.queries?.length == 0 && resp?.resources?.results?.products?.length == 0){
              document.querySelector('.view-all-lnk').style.display = 'none';
              var container = document.querySelector('.search-fill-case');
              var existingDiv = container.querySelector('.no-result-message');
              if (existingDiv) {    
                  existingDiv.remove();
              }
              var newDiv = document.createElement('div');
              var searchValue = document.querySelector('.autocomplete-search-textbox').value; 
              if( searchValue != "" ){
                searchValue = searchValue.trim();
              }
              newDiv.innerHTML = `No matching searches for <span class="search-term">'`+ searchValue + `'</span>.`;
              newDiv.className = 'no-result-message';
              container.prepend(newDiv);
            }
            else{
              document.querySelector('.view-all-lnk').style.display = 'block';
              var container = document.querySelector('.search-fill-case');
              var existingDiv = container.querySelector('.no-result-message');
              if (existingDiv) {    
                  existingDiv.remove();
              }
            }
          }
          document.querySelector(".search-fill-case").style.display = "block";
        }
      }
    }
  }

  let timer;
  
  function getSearchDebounce(e){
    clearTimeout(timer)
      timer = setTimeout(() => {
        getSearch(e)
      }, 50);
  }

  /* Quick Search API Changes - End */ 

  $(".autocomplete-search-result-outer").hover(
    function () {
      $(this).addClass("active");
      $(".autocomplete-search-result-outer").css("display", "block");
    },
    function () {
      $(this).removeClass("active");
      $(".autocomplete-search-result-outer").css("display", "none");

    }
  );

  /* JS code for hide Quick Search Popup after tab Start */

  $(window).keyup(function (e) {
    if(  $(".autocomplete-search-result-outer").hasClass('active') ){
      var code = (e.keyCode ? e.keyCode : e.which);
      console.log("length:: ", $('.quick-search-kobo:focus').length)
      if ( $('.quick-search-kobo:focus').length == 0 ) {
          $(".autocomplete-search-result-outer").css("display", "none");
          $(".autocomplete-search-result-outer").removeClass('active');
      }
    }
    else{
          $(".autocomplete-search-result-outer").css("display", "none");
          $(".autocomplete-search-result-outer").removeClass('active');
    }
    
  });
  
   /* JS code for hide Quick Search Popup after tab end */

  /* Arrow Key up/down Accessiblity Meatobject Start   */
    $('.autocomplete-search-textbox').keydown(function (e) {
      var code = (e.keyCode ? e.keyCode : e.which);
      if (code == 40 ) {
      
    	if( $('.search-result-box.default-case.search-empty-case').css('display') == "block" ){
    		$('.search-result-box.default-case.search-empty-case').find('li').first().find('a').focus();
    	}
    	else{
    		$('.result-box.full-suggestion-case').find('li').first().find('a').focus();
    	}
      }
    });
  
    $('.quick-search-kobo a').keydown(function(e) {
      if (e.which === 38) { // Up arrow key
        $(this).parent('li').prev('li').find('a').focus()
      }
  
      if (e.which === 40) { // Down arrow key
        $(this).parent('li').next('li').find('a').focus()
        if($(this).parent('li').next('li').length==0){
            $(".autocomplete-search-result-outer").css("display", "none");
            $(".autocomplete-search-result-outer").removeClass('active');
          
        }  
      }
  });
  
  /* Arrow Key up/down Accessiblity Meatobject End   */
  
});

/* END ::: Search */